import { Clock, Mail, MapPin, Phone, Send } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/storefront/PageHeader";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { usePageContent, useSite } from "@/context/SiteContext";
import { API, errMsg } from "@/lib/api";

export default function Contact() {
  const content = usePageContent("contact_page");
  const { settings } = useSite();
  const business = settings?.business || {};
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await API.post("/contact", form);
      toast.success("Thank you — your message has been sent. We'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo title={content?.seo_title} description={content?.seo_description} />
      <PageHeader title={content?.title || "Contact Us"} subtitle={content?.intro} testId="contact-header" />
      <div className="max-w-6xl mx-auto px-4 pb-16 grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2 space-y-4">
          {business.phone && (
            <a href={`tel:${business.phone}`} className="block" data-testid="contact-phone-link">
              <Card className="hover:border-primary transition-colors">
                <CardContent className="p-5 flex items-center gap-4">
                  <div className="p-3 rounded-full bg-primary/10 text-primary"><Phone className="h-5 w-5" /></div>
                  <div><p className="text-sm text-muted-foreground">Call us</p><p className="font-semibold">{business.phone}</p></div>
                </CardContent>
              </Card>
            </a>
          )}
          {business.email && (
            <a href={`mailto:${business.email}`} className="block" data-testid="contact-email-link">
              <Card className="hover:border-primary transition-colors">
                <CardContent className="p-5 flex items-center gap-4">
                  <div className="p-3 rounded-full bg-primary/10 text-primary"><Mail className="h-5 w-5" /></div>
                  <div><p className="text-sm text-muted-foreground">Email us</p><p className="font-semibold break-all">{business.email}</p></div>
                </CardContent>
              </Card>
            </a>
          )}
          {business.address && (
            <Card data-testid="contact-address-card">
              <CardContent className="p-5 flex items-start gap-4">
                <div className="p-3 rounded-full bg-primary/10 text-primary"><MapPin className="h-5 w-5" /></div>
                <div><p className="text-sm text-muted-foreground">Visit us</p><p className="font-semibold">{business.address}</p></div>
              </CardContent>
            </Card>
          )}
          {(business.opening_hours || []).length > 0 && (
            <Card data-testid="contact-hours-card">
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-3 rounded-full bg-primary/10 text-primary"><Clock className="h-5 w-5" /></div>
                  <p className="font-semibold">Opening Hours</p>
                </div>
                <ul className="space-y-1.5 text-sm text-muted-foreground ml-14">
                  {business.opening_hours.map((h, i) => (
                    <li key={i} className="flex justify-between gap-6"><span>{h.day}</span><span>{h.hours}</span></li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
          {content?.map_embed && (
            <div className="rounded-xl overflow-hidden border aspect-video" data-testid="contact-map" dangerouslySetInnerHTML={{ __html: content.map_embed }} />
          )}
        </div>

        <Card className="lg:col-span-3 h-fit" data-testid="contact-form-card">
          <CardContent className="p-6 sm:p-8">
            <h2 className="font-display text-2xl tracking-tight mb-6">Send us a message</h2>
            <form onSubmit={submit} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="contact-name">Your name *</Label>
                  <Input id="contact-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-phone">Phone</Label>
                  <Input id="contact-phone" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-email">Email *</Label>
                <Input id="contact-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-message">Message *</Label>
                <Textarea id="contact-message" required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message-input" />
              </div>
              <Button type="submit" className="rounded-full px-8" disabled={sending} data-testid="contact-submit-btn">
                <Send className="mr-2 h-4 w-4" /> {sending ? "Sending…" : "Send message"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
